import React, { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { Save, Send } from "lucide-react";
import ScoutingScaleReference from "@/components/ScoutingScaleReference";

const GRADES_20_80 = [20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80];

const TOOL_SETS = {
  position_player: [
    { key: "hit", label: "Hit (Present/Future)" },
    { key: "power", label: "Raw Power" },
    { key: "game_power", label: "Game Power" },
    { key: "run", label: "Run (60-yd corollary)" },
    { key: "field", label: "Field" },
    { key: "arm", label: "Arm" },
  ],
  pitcher: [
    { key: "fastball", label: "Fastball" },
    { key: "curveball", label: "Curveball" },
    { key: "slider", label: "Slider" },
    { key: "changeup", label: "Changeup" },
    { key: "control", label: "Control" },
    { key: "command", label: "Command" },
  ],
  catcher: [
    { key: "hit", label: "Hit" },
    { key: "power", label: "Power" },
    { key: "receiving", label: "Receiving / Framing" },
    { key: "catch_throw", label: "Catch & Throw" },
    { key: "arm", label: "Arm" },
    { key: "run", label: "Run" },
  ],
};

const ARCHETYPE_LABEL = {
  position_player: "Position Player",
  pitcher: "Pitcher",
  catcher: "Catcher",
};

function gradeColor(g) {
  if (g == null) return "bg-slate-200 text-slate-500";
  if (g >= 60) return "bg-[#E63946] text-white";
  if (g >= 50) return "bg-[#0A192F] text-white";
  if (g >= 40) return "bg-amber-200 text-amber-900";
  return "bg-slate-300 text-slate-700";
}

/**
 * 20-80 scouting evaluation form.
 * Supports inline create + edit + submit.
 *
 * Props:
 *  - playerId (required)
 *  - playerPosition (optional, used to pick default archetype)
 *  - camps: list of camps to pick from
 *  - initial: existing evaluation document (for edit mode)
 *  - onSaved: callback
 */
export default function EvaluationForm({ playerId, playerPosition, camps = [], initial = null, onSaved }) {
  const detectArchetype = () => {
    if (initial?.role_archetype) return initial.role_archetype;
    if (playerPosition === "P") return "pitcher";
    if (playerPosition === "C") return "catcher";
    return "position_player";
  };

  const [archetype, setArchetype] = useState(detectArchetype());
  const [campId, setCampId] = useState(initial?.camp_id || "");
  const [eventDate, setEventDate] = useState(
    initial?.event_date?.slice(0, 10) || new Date().toISOString().slice(0, 10)
  );
  const [tools, setTools] = useState(initial?.tools || {});
  const [ofv, setOfv] = useState(initial?.overall_future_value || "");
  const [report, setReport] = useState(initial?.written_report || "");
  const [strengths, setStrengths] = useState(initial?.strengths || "");
  const [development, setDevelopment] = useState(initial?.development_areas || "");
  const [recommendedLevel, setRecommendedLevel] = useState(initial?.recommended_level || "");
  const [videoUrl, setVideoUrl] = useState("");
  const [videos, setVideos] = useState(initial?.video_urls || []);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setArchetype(detectArchetype());
    // eslint-disable-next-line
  }, [playerPosition, initial]);

  const setTool = (key, val) => setTools({ ...tools, [key]: Number(val) });

  const save = async (status) => {
    setError("");
    setBusy(true);
    try {
      const camp = camps.find((c) => c.id === campId);
      const payload = {
        player_id: playerId,
        camp_id: campId || null,
        event_date: eventDate ? new Date(eventDate).toISOString() : null,
        position_evaluated: playerPosition || null,
        role_archetype: archetype,
        tools,
        overall_future_value: ofv ? Number(ofv) : null,
        written_report: report || null,
        strengths: strengths || null,
        development_areas: development || null,
        recommended_level: recommendedLevel || null,
        video_urls: videos,
        status,
      };
      const payloadFiltered = Object.fromEntries(
        Object.entries(payload).filter(([, v]) => v !== "" && v !== undefined)
      );
      if (initial?.id) {
        await api.patch(`/evaluations/${initial.id}`, payloadFiltered);
      } else {
        await api.post("/evaluations", payloadFiltered);
      }
      if (onSaved) await onSaved();
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const addVideo = () => {
    if (videoUrl) {
      setVideos([...videos, videoUrl]);
      setVideoUrl("");
    }
  };
  const removeVideo = (idx) => setVideos(videos.filter((_, i) => i !== idx));

  return (
    <div className="flex flex-col lg:flex-row lg:gap-6 lg:items-start">
      <div data-testid="evaluation-form" className="flex-1 min-w-0 bg-white border border-slate-200 rounded-sm p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-overline text-[#E63946]">20-80 Scouting Report</div>
          <h3 className="font-display text-2xl uppercase tracking-tight mt-1">
            {initial ? "Edit Evaluation" : "New Evaluation"}
          </h3>
        </div>
        {initial?.status && (
          <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 ${initial.status === "submitted" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
            {initial.status}
          </span>
        )}
      </div>

      {error && <div className="border-l-4 border-[#E63946] bg-red-50 p-3 text-sm text-[#E63946]">{error}</div>}

      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <label className="label-overline">Archetype</label>
          <select data-testid="eval-archetype" value={archetype} onChange={(e) => { setArchetype(e.target.value); setTools({}); }} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
            {Object.entries(ARCHETYPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="label-overline">Event / Camp</label>
          <select data-testid="eval-camp" value={campId} onChange={(e) => setCampId(e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
            <option value="">— Independent —</option>
            {camps.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
        </div>
        <div>
          <label className="label-overline">Event Date</label>
          <input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
        </div>
      </div>

      {/* Tools grid */}
      <div>
        <div className="label-overline mb-2">Tools (20-80 scale · 50 = MLB average)</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {TOOL_SETS[archetype].map((t) => (
            <div key={t.key} className="border border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold">{t.label}</label>
                <span className={`px-2 py-0.5 text-xs font-semibold rounded-sm ${gradeColor(tools[t.key])}`}>
                  {tools[t.key] ?? "—"}
                </span>
              </div>
              <select
                data-testid={`tool-${t.key}`}
                value={tools[t.key] ?? ""}
                onChange={(e) => setTool(t.key, e.target.value)}
                className="mt-2 w-full px-3 py-1.5 border border-slate-300 rounded-sm bg-white text-sm"
              >
                <option value="">— Grade —</option>
                {GRADES_20_80.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
          ))}
        </div>
      </div>

      {/* OFV */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="border-l-4 border-[#E63946] bg-slate-50 p-4">
          <label className="label-overline text-[#E63946]">Overall Future Value (OFV)</label>
          <select
            data-testid="eval-ofv"
            value={ofv}
            onChange={(e) => setOfv(e.target.value)}
            className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-base font-data"
          >
            <option value="">— Grade —</option>
            {GRADES_20_80.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <p className="text-[11px] text-slate-500 mt-2">
            The headline grade. Combined with verified metrics by the CBR Overall Rating engine.
          </p>
        </div>
        <div>
          <label className="label-overline">Recommended Level</label>
          <select value={recommendedLevel} onChange={(e) => setRecommendedLevel(e.target.value)} className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
            <option value="">—</option>
            <option value="NCAA D1">NCAA Division I</option>
            <option value="NCAA D2">NCAA Division II</option>
            <option value="NCAA D3">NCAA Division III</option>
            <option value="NAIA">NAIA</option>
            <option value="JUCO">JUCO</option>
            <option value="USports">U Sports (Canada)</option>
            <option value="MLB Draft Top 5R">MLB Draft Top 5 Rounds</option>
            <option value="MLB Draft 6-10R">MLB Draft 6-10 Rounds</option>
            <option value="Develop">Continue Developing</option>
          </select>
        </div>
      </div>

      {/* Notes */}
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="label-overline">Strengths</label>
          <textarea data-testid="eval-strengths" rows="3" value={strengths} onChange={(e) => setStrengths(e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
        </div>
        <div>
          <label className="label-overline">Development Areas</label>
          <textarea data-testid="eval-development" rows="3" value={development} onChange={(e) => setDevelopment(e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
        </div>
        <div className="sm:col-span-2">
          <label className="label-overline">Written Report</label>
          <textarea data-testid="eval-report" rows="5" value={report} onChange={(e) => setReport(e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" placeholder="Comprehensive scouting notes, mechanics, mental approach, projection…" />
        </div>
      </div>

      {/* Videos */}
      <div>
        <label className="label-overline">Video URLs (optional)</label>
        <div className="flex gap-2 mt-1">
          <input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://… (YouTube, Hudl, Vimeo)" className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          <button type="button" onClick={addVideo} className="px-4 py-2 border border-slate-300 text-xs font-semibold uppercase tracking-wider rounded-sm">Add</button>
        </div>
        {videos.length > 0 && (
          <ul className="mt-2 space-y-1">
            {videos.map((v, i) => (
              <li key={i} className="flex items-center justify-between text-xs bg-slate-50 px-2 py-1">
                <a href={v} target="_blank" rel="noreferrer" className="text-[#0A192F] hover:text-[#E63946] truncate">{v}</a>
                <button type="button" onClick={() => removeVideo(i)} className="text-[#E63946] ml-2">×</button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex justify-end gap-3 border-t border-slate-200 pt-4">
        <button
          data-testid="eval-save-draft"
          type="button"
          disabled={busy}
          onClick={() => save("draft")}
          className="inline-flex items-center gap-1 px-5 py-2 border border-slate-300 text-xs font-semibold uppercase tracking-wider rounded-sm"
        >
          <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save Draft"}
        </button>
        <button
          data-testid="eval-submit"
          type="button"
          disabled={busy}
          onClick={() => save("submitted")}
          className="inline-flex items-center gap-1 px-5 py-2 bg-[#E63946] hover:bg-[#D90429] text-white text-xs font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60"
        >
          <Send className="w-4 h-4" /> {busy ? "Submitting…" : "Submit Report"}
        </button>
      </div>
      </div>
      <ScoutingScaleReference />
    </div>
  );
}
